import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FolderOpen, Add, Save, Search, Clear } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { useRutasObrasSocial } from '../hooks/useRutasObrasSocial';
import { obrasSocialesAPI } from '../../../core/api/api';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

// Parsea las obras sociales de una ruta como array de nombres
const parseObrasSociales = (ruta) =>
  ruta.nombreObraSocial ? ruta.nombreObraSocial.split(';').filter(Boolean) : [];

const GrillaRutasObrasSocial = () => {
  const { rutas, loading, cargarRutas, guardarRuta, actualizarRuta, eliminarRuta } = useRutasObrasSocial();
  const [obrasSociales, setObrasSociales] = useState([]);
  const [filtroOS, setFiltroOS] = useState('');
  const [modal, setModal] = useState(null); // null | { mode: 'nuevo' | 'editar', ruta? }
  const [rutaTexto, setRutaTexto] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    cargarRutas();
    obrasSocialesAPI.getObrasSociales().then((r) => setObrasSociales(r.data || [])).catch(() => {});
  }, [cargarRutas]);

  const rutasFiltradas = useMemo(() => {
    if (!filtroOS) return rutas;
    return rutas.filter((r) => {
      const ids = r.idObraSocial ? r.idObraSocial.split(';').map(Number) : [];
      return ids.includes(Number(filtroOS));
    });
  }, [rutas, filtroOS]);

  const abrirNuevo = () => { setRutaTexto(''); setSelectedIds([]); setModal({ mode: 'nuevo' }); };

  const abrirEditar = (ruta) => {
    setRutaTexto(ruta.ruta || '');
    setSelectedIds(ruta.idObraSocial ? ruta.idObraSocial.split(';').map(Number) : []);
    setModal({ mode: 'editar', ruta });
  };

  const toggleObraSocial = useCallback((id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]), []);

  const handleGuardar = async (e) => {
    e.preventDefault();
    const selected = obrasSociales.filter((os) => selectedIds.includes(os.idObraSocial));
    const data = {
      ruta: rutaTexto.trim(),
      idObraSocial: selected.map((os) => os.idObraSocial).join(';'),
      nombreObraSocial: selected.map((os) => os.obraSocial).join(';'),
    };
    try {
      if (modal.mode === 'editar') {
        await actualizarRuta(modal.ruta.id, data);
        setToast({ message: 'Ruta actualizada con éxito.', type: 'success' });
      } else {
        await guardarRuta(data);
        setToast({ message: 'Ruta guardada con éxito.', type: 'success' });
      }
      setModal(null);
    } catch {
      setToast({ message: 'Error al guardar la ruta.', type: 'error' });
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminarRuta(confirmEliminar.id);
      setToast({ message: 'Ruta eliminada con éxito.', type: 'success' });
    } catch {
      setToast({ message: 'Error al eliminar la ruta.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <FolderOpen sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Rutas de Guardado DropBox
          </h2>
          <span className="page-subtitle">Configuración de rutas por obra social</span>
        </div>

        {/* Modal */}
        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modal?.mode === 'editar' ? 'Editar Ruta' : 'Nueva Ruta'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.5rem' }}>
              <div style={{ width: '100%', marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>Ruta *</label>
                <input
                  type="text"
                  className="form-control"
                  value={rutaTexto}
                  onChange={(e) => setRutaTexto(e.target.value)}
                  placeholder="Ej: /DropBox/ObraSocial"
                  required
                  autoFocus
                />
              </div>
              <div style={{ width: '100%' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>Obras Sociales *</label>
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '0.75rem' }}>
                  {obrasSociales.map((os) => (
                    <div key={os.idObraSocial} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.3rem 0', borderBottom: '1px solid #f0f0f0' }}>
                      <input
                        type="checkbox"
                        id={`os-${os.idObraSocial}`}
                        checked={selectedIds.includes(os.idObraSocial)}
                        onChange={() => toggleObraSocial(os.idObraSocial)}
                        style={{ marginTop: '3px', flexShrink: 0 }}
                      />
                      <label htmlFor={`os-${os.idObraSocial}`} style={{ margin: 0, lineHeight: '1.4', wordBreak: 'break-word', cursor: 'pointer' }}>
                        {os.obraSocial}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !rutaTexto.trim() || selectedIds.length === 0}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {modal?.mode === 'editar' ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Filtros */}
        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtroOS ? 'filter-active' : ''}`}>
                <label className="filter-label">Obra Social</label>
                <select className="filter-input" value={filtroOS} onChange={(e) => setFiltroOS(e.target.value)}>
                  <option value="">Todas</option>
                  {obrasSociales.map((os) => <option key={os.idObraSocial} value={os.idObraSocial}>{os.obraSocial}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => {}} disabled={loading}><Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar</Button>
                <Button className="btn-clear" onClick={() => setFiltroOS('')}><Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Resultados */}
        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Rutas configuradas</h3></div>
            <div className="results-info">
              <span className="results-count">{rutasFiltradas.length} rutas configuradas</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={abrirNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nueva Ruta
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '35%' }}>Ruta</th>
                  <th>Obras Sociales</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rutasFiltradas.length === 0 ? (
                  <tr><td colSpan={3} className="empty-state">{loading ? 'Cargando...' : 'No hay rutas configuradas'}</td></tr>
                ) : rutasFiltradas.map((ruta) => (
                  <tr key={ruta.id}>
                    <td style={{ fontWeight: 500, wordBreak: 'break-all', fontSize: '0.875rem' }}>
                      📁 {ruta.ruta}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {parseObrasSociales(ruta).map((nombre) => (
                          <span key={nombre} style={{
                            display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '12px',
                            fontSize: '0.75rem', fontWeight: 500,
                            background: '#e0f2fe', color: '#0369a1',
                            border: '1px solid #7dd3fc',
                          }}>
                            {nombre}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="action-cell">
                      <RowActions onEdit={() => abrirEditar(ruta)} onDelete={() => setConfirmEliminar({ id: ruta.id, label: ruta.ruta })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar ruta"
        message={`¿Eliminar la ruta "${confirmEliminar?.label}"?`}
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaRutasObrasSocial;
